import { z } from "zod";

import type { PicksResponse } from "../../shared/types";

export type FetchPicksOptions = {
  leagues?: string;
  maxMargin?: number;
  minFairProbability?: number;
};

/**
 * Fetch ranked picks + doubles from the worker.
 *
 * The response is validated with a Zod schema instead of a hand-rolled type
 * guard so upstream contract drift is caught loudly at the boundary rather
 * than silently corrupting the UI.
 */
export async function fetchPicks(options: FetchPicksOptions = {}): Promise<PicksResponse> {
  const params = new URLSearchParams();
  if (options.leagues) params.set("leagues", options.leagues);
  if (options.maxMargin !== undefined) params.set("maxMargin", options.maxMargin.toString());
  if (options.minFairProbability !== undefined)
    params.set("minFairProbability", options.minFairProbability.toString());

  const url = params.toString() ? `/api/picks?${params}` : "/api/picks";
  const response = await fetch(url, { headers: { accept: "application/json" } });
  const payload = (await response.json().catch(() => null)) as unknown;

  if (!response.ok) {
    const message = ErrorPayloadSchema.safeParse(payload).data?.error ?? `HTTP ${response.status}`;
    throw new Error(message);
  }

  const parsed = PicksResponseSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error(`Invalid API response: ${parsed.error.issues[0]?.message ?? "schema mismatch"}`);
  }

  return parsed.data;
}

const ErrorPayloadSchema = z.object({ error: z.string().min(1) });

const OutcomeSchema = z.enum(["home", "draw", "away"]);

const MatchOddsSchema = z.object({
  home: z.number().positive(),
  draw: z.number().positive(),
  away: z.number().positive(),
});

const MatchSchema = z.object({
  externalId: z.string(),
  kickoffTime: z.string(),
  league: z.string(),
  country: z.string().optional(),
  leagueId: z.string().optional(),
  leagueLabel: z.string().optional(),
  homeTeam: z.string(),
  awayTeam: z.string(),
  odds: MatchOddsSchema,
  margin: z.number(),
});

const ModelInputsSchema = z.object({
  eloHome: z.number().optional(),
  eloAway: z.number().optional(),
  formHome: z.number().optional(),
  formAway: z.number().optional(),
  lambdaHome: z.number(),
  lambdaAway: z.number(),
  confidence: z.number(),
});

const WeatherSignalSchema = z.object({
  temperatureC: z.number(),
  windSpeedMs: z.number(),
  precipMm: z.number(),
  conditionMain: z.string(),
  conditionDescription: z.string(),
  severity: z.number(),
});

const VerdictSchema = z.enum(["best", "good", "ok", "avoid"]);
const PrimaryMetricSchema = z.enum(["modelEv", "evScore", "fairProbability"]);
const DataSourceSchema = z.enum(["norskTipping", "consensus", "model", "weather"]);
const ConfidenceSchema = z.object({
  sources: z.array(DataSourceSchema),
  missing: z.array(DataSourceSchema),
  score: z.number(),
});

const ProbabilityPickSchema = z.object({
  id: z.string(),
  outcome: OutcomeSchema,
  selectionName: z.string(),
  match: MatchSchema,
  decimalOdds: z.number().positive(),
  impliedProbability: z.number(),
  fairProbability: z.number(),
  fairOdds: z.number().positive(),
  leagueId: z.string().optional(),
  leagueLabel: z.string().optional(),
  consensusFairProbability: z.number().optional(),
  evScore: z.number().optional(),
  modelFairProbability: z.number().optional(),
  modelEv: z.number().optional(),
  highConviction: z.boolean().optional(),
  modelInputs: ModelInputsSchema.optional(),
  weather: WeatherSignalSchema.optional(),
  verdict: VerdictSchema,
  primaryMetric: PrimaryMetricSchema,
  priorityScore: z.number(),
  confidence: ConfidenceSchema,
});

const DoublePickSchema = z.object({
  id: z.string(),
  legs: z.tuple([ProbabilityPickSchema, ProbabilityPickSchema]),
  combinedDecimalOdds: z.number().positive(),
  combinedFairProbability: z.number(),
  combinedFairOdds: z.number().positive(),
  combinedEv: z.number(),
  combinedConsensusEv: z.number().optional(),
  modelCombinedFairProbability: z.number().optional(),
  modelCombinedEv: z.number().optional(),
  combinedHighConviction: z.boolean().optional(),
  kellyStakeFraction: z.number(),
  combinedVerdict: VerdictSchema,
  combinedConfidence: ConfidenceSchema,
});

const DayPicksSchema = z.object({
  daysAhead: z.number().int().min(0),
  date: z.string(),
  picks: z.array(ProbabilityPickSchema),
});

const PicksResponseSchema = z.object({
  days: z.array(DayPicksSchema),
  safeDoubles: z.array(DoublePickSchema),
  valueDoubles: z.array(DoublePickSchema),
  fetchedAt: z.string(),
  meta: z
    .object({
      consensusEvents: z.number(),
      consensusEnabled: z.boolean(),
      daysAhead: z.number().int().min(1),
    })
    .passthrough()
    .optional(),
}) satisfies z.ZodType<PicksResponse>;
